<template>
  <div class="space-y-4">
    <div v-for="group in navigation" :key="group.group">
      <p v-if="!collapsed" class="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-sidebar-muted">
        {{ group.group }}
      </p>
      <div class="space-y-0.5">
        <template v-for="item in group.items" :key="item.name">
          <button
              v-if="item.children && collapsed"
              type="button"
              :class="classesFor(isChildActive(item))"
              :title="item.name"
              @click="$emit('expand')">
            <component :is="item.icon" class="h-4 w-4 shrink-0"/>
          </button>

          <Disclosure v-else-if="item.children" v-slot="{ open }" :default-open="item.defaultOpen || isChildActive(item)">
            <DisclosureButton
                class="group flex h-9 w-full items-center gap-2 rounded-md px-2 text-sm font-medium text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground">
              <component :is="item.icon" class="h-4 w-4 shrink-0"/>
              <span class="min-w-0 flex-1 truncate text-left">{{ item.name }}</span>
              <ChevronRightIcon
                  :class="[open ? 'rotate-90' : '', 'h-4 w-4 shrink-0 transition-transform']"/>
            </DisclosureButton>
            <DisclosurePanel class="ml-4 space-y-0.5 border-l border-sidebar-border pl-2 pt-0.5">
              <router-link
                  v-for="child in item.children"
                  :key="child.name"
                  :to="child.href"
                  :class="childClasses(child.href)"
                  @click="$emit('navigate')">
                <span class="min-w-0 flex-1 truncate">{{ child.name }}</span>
              </router-link>
            </DisclosurePanel>
          </Disclosure>

          <router-link
              v-else
              :to="item.href"
              :class="itemClasses(item)"
              :title="collapsed ? item.name : null"
              @click="$emit('navigate')">
            <component :is="item.icon" class="h-4 w-4 shrink-0"/>
            <span v-if="!collapsed" class="min-w-0 flex-1 truncate">{{ item.name }}</span>
            <span v-if="item.count && !collapsed"
                  class="ml-auto rounded-full bg-brand-green px-2 py-0.5 text-xs font-medium leading-4 text-brand-dark">
              {{ item.count }}
            </span>
            <span v-else-if="item.count"
                  class="absolute -right-0.5 -top-0.5 min-w-[1rem] rounded-full bg-brand-green px-1 text-center text-[10px] font-semibold leading-4 text-brand-dark">
              {{ item.count }}
            </span>
          </router-link>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import {Disclosure, DisclosureButton, DisclosurePanel} from '@headlessui/vue'
import {ChevronRightIcon} from '@heroicons/vue/solid'

export default {
  name: 'SidebarContent',
  components: {Disclosure, DisclosureButton, DisclosurePanel, ChevronRightIcon},
  props: {
    navigation: {type: Array, required: true},
    currentPath: {type: String, required: true},
    collapsed: {type: Boolean, default: false},
  },
  emits: ['navigate', 'expand'],
  methods: {
    section(path) {
      return (path || '').split('/').filter(Boolean)[0] || ''
    },
    classesFor(active) {
      return [
        'group relative flex h-9 w-full items-center rounded-md text-sm font-medium',
        this.collapsed ? 'justify-center px-0' : 'gap-2 px-2',
        active
            ? 'bg-sidebar-accent text-sidebar-accent-foreground'
            : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
      ]
    },
    itemClasses(item) {
      const href = item.href
      let active
      if (item.matchPrefix) {
        // Actif uniquement sur ce préfixe précis (ex. /global_transaction/day/…)
        active = this.currentPath.startsWith(item.matchPrefix)
      } else if (href === '/') {
        active = this.currentPath === '/'
      } else {
        // Top-level items stay active on their sub-pages (detail views included).
        active = this.section(this.currentPath) === this.section(href)
        if (active && item.excludePrefix) {
          active = !this.currentPath.startsWith(item.excludePrefix)
        }
      }
      return this.classesFor(active)
    },
    childClasses(href) {
      const active = this.currentPath === href || this.currentPath.startsWith(href + '/')
      return this.classesFor(active)
    },
    isChildActive(item) {
      return (item.children || []).some((child) => this.section(child.href) === this.section(this.currentPath))
    },
  },
}
</script>
